import _isNil from "lodash/isNil";
import { getAPY, getAPYAnnotation, getBalanceToAvoidFees, getBonusAmount, getBonusDetails, getDirectDepositWaive, getFederallyInsured, getMinimumBalance, getMonthlyFee, getMonthlyFeeSubCopy, getOutOfNetworkATMFee, getSubProductType, getMinimumOpeningDeposit } from '../selectors/banking';
import { formatValue } from '../utils/format';
import { getMinimumBalanceLabel } from '../utils/banking';
import { BANKING_SUB_PRODUCT_TYPES, FEDERALLY_INSURED_ANNOTATIONS } from '../constants';
import { BANKING_CD_TERM_LENGTHS, FORMATS, VALUE_SIZES } from '../constants/drivers';
/**
 * Get the APY driver.
 * @see {@link https://github.com/NerdWallet/banking-client/blob/89e0a965e6248f2b99f28f8a0c93e4bf3e6ee755/client/lib/reducers/marketplace-decorator.js#L154}
 * @param {object} offer - The marketplace offer.
 * @param {string} [termLength] - The term length for labeling.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverApy = function getDriverApy(offer, termLength) {
  var minimumBalance = getMinimumBalance(offer);
  var apy = getAPY(offer);
  var label;
  var id;

  switch (termLength) {
    case BANKING_CD_TERM_LENGTHS.ONE_YEAR:
      id = 'apy1';
      label = '1-year APY';
      break;

    case BANKING_CD_TERM_LENGTHS.THREE_YEAR:
      id = 'apy3';
      label = '3-year APY';
      break;

    case BANKING_CD_TERM_LENGTHS.FIVE_YEAR:
      id = 'apy5';
      label = '5-year APY';
      break;

    default:
      id = 'apy';
      label = 'APY';
  }

  return {
    description: getSubProductType(offer) !== BANKING_SUB_PRODUCT_TYPES.CD && !_isNil(minimumBalance) && !_isNil(apy) ? "With ".concat(formatValue(minimumBalance, minimumBalance % 1 !== 0 ? FORMATS.CURRENCY_WITH_CENTS : FORMATS.CURRENCY), " min. balance for APY") : null,
    id: id,
    label: label,
    value: apy,
    valueFormat: FORMATS.STRING,
    valueSize: VALUE_SIZES.LARGE,
    valueTooltip: getAPYAnnotation(offer)
  };
};
/**
 * Get the bonus driver.
 * @see {@link https://github.com/NerdWallet/banking-client/blob/master/client/lib/reducers/marketplace-decorator.js#L78}
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverBonus = function getDriverBonus(offer) {
  var valueTooltip;
  var description;
  var bonusDetails = getBonusDetails(offer);

  if (bonusDetails && bonusDetails.length > 60) {
    valueTooltip = bonusDetails;
    description = 'Requirements to qualify';
  } else {
    description = bonusDetails;
  }

  return {
    description: description,
    id: 'bonus',
    label: 'Bonus',
    value: getBonusAmount(offer),
    valueFormat: FORMATS.CURRENCY,
    valueTooltip: valueTooltip
  };
};
/**
 * Get the minimum balance driver.
 * @see {@link https://github.com/NerdWallet/banking-client/blob/4423d3790f67e8f61f68a889a51079a753c40a24/client/lib/reducers/map-offers.js#L38}
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverMinimumBalance = function getDriverMinimumBalance(offer) {
  var federallyInsured = getFederallyInsured(offer);
  return {
    description: FEDERALLY_INSURED_ANNOTATIONS[federallyInsured],
    id: 'minimumBalance',
    label: getMinimumBalanceLabel(offer),

    /**
     * Ensure we output $0 even for null values.
     * @see {@link https://github.com/NerdWallet/banking-client/blob/9b34b1acc7632a7ee9f32af89607071c0d51f78f/client/utils/data-format-utils.js#L8}
     */
    value: getMinimumBalance(offer) || 0,
    valueFormat: FORMATS.CURRENCY,
    valueSize: VALUE_SIZES.LARGE
  };
};
/**
 * Get the monthly fee driver.
 * @see {@link https://github.com/NerdWallet/banking-client/blob/89e0a965e6248f2b99f28f8a0c93e4bf3e6ee755/client/lib/reducers/marketplace-decorator.js#L107-L122}
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverMonthlyFee = function getDriverMonthlyFee(offer) {
  var description;
  var balanceToAvoidFees = getBalanceToAvoidFees(offer);
  var directDepositWaive = getDirectDepositWaive(offer);
  /**
   * Ensure we output $0 even for null values.
   * @see {@link https://github.com/NerdWallet/banking-client/blob/9b34b1acc7632a7ee9f32af89607071c0d51f78f/client/utils/data-format-utils.js#L8}
   */

  var monthlyFee = getMonthlyFee(offer) || 0;
  var subCopyDescription = getMonthlyFeeSubCopy(offer);

  if (subCopyDescription) {
    description = subCopyDescription;
  } else if (!_isNil(balanceToAvoidFees) && directDepositWaive > 0) {
    description = "Waived with direct deposit or ".concat(formatValue(balanceToAvoidFees, FORMATS.CURRENCY), " minimum balance");
  } else if (monthlyFee !== 0 && !_isNil(balanceToAvoidFees)) {
    description = "Waived with ".concat(formatValue(balanceToAvoidFees, FORMATS.CURRENCY), " minimum balance");
  }

  return {
    description: description,
    id: 'monthlyFee',
    label: 'Monthly fee',
    value: monthlyFee,
    valueFormat: FORMATS.CURRENCY_WITH_CENTS,
    valueSize: VALUE_SIZES.LARGE
  };
};
/**
 * Get "out-of-network fee" driver.
 * @see {@link https://github.com/NerdWallet/banking-client/blob/4423d3790f67e8f61f68a889a51079a753c40a24/client/lib/reducers/map-offers.js#L62}
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "quick fact" driver.
 */

export var getDriverOutOfNetworkAtmFee = function getDriverOutOfNetworkAtmFee(offer) {
  return {
    id: 'outOfNetworkATMFee',
    label: 'Out-of-network ATM fee',

    /**
     * Ensure we output $0 even for null values.
     * @see {@link https://github.com/NerdWallet/banking-client/blob/9b34b1acc7632a7ee9f32af89607071c0d51f78f/client/utils/data-format-utils.js#L8}
     * @ignore
     */
    value: getOutOfNetworkATMFee(offer) || 0,
    valueFormat: FORMATS.CURRENCY_WITH_CENTS,
    valueSize: VALUE_SIZES.LARGE
  };
};
/**
 * Get "minimum opening deposit" driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The "minimum opening deposit" driver.
 */

export var getDriverMinimumOpeningDeposit = function getDriverMinimumOpeningDeposit(offer) {
  return {
    id: 'minimumOpeningDeposit',
    label: 'Min. opening deposit',

    /**
     * Ensure we output $0 even for null values.
     * @see {@link https://github.com/NerdWallet/banking-client/blob/9b34b1acc7632a7ee9f32af89607071c0d51f78f/client/utils/data-format-utils.js#L8}
     * @ignore
     */
    value: getMinimumOpeningDeposit(offer) || 0,
    valueFormat: FORMATS.CURRENCY,
    valueSize: VALUE_SIZES.LARGE
  };
};